import { toCurrency } from '@/utils';
import { toFormattedDate } from '@/utils/date';

export const PER_PAGE_OPTIONS = [25, 50, 100];

export const SHIFT_FIELD_DEFINITIONS = [
  {
    key: 'employee_code',
    label: 'Employee Code',
  },
  {
    key: 'full_name',
    label: 'Employee',
    sortable: false,
  },
  {
    key: 'worked_on',
    label: 'Date',
    formatter: value => {
      if (value) {
        return toFormattedDate(value);
      }
    },
  },
  {
    key: 'hours',
    label: 'Hours',
    formatter: value => value.toLocaleString('en-GB', { maximumFractionDigits: 2 }),
  },
  {
    key: 'wages',
    label: 'Earned Wages',
    formatter: (value, _key, item) => toCurrency(value, item.currency),
  },
  {
    key: 'tips',
    label: 'Tips',
    formatter: (value, _key, item) => toCurrency(value, item.currency),
  },
  {
    key: 'wages_per_hour',
    label: 'Rate',
    formatter: (_value, _key, item) => {
      if (item.hours === 0) {
        return toCurrency(0, item.currency);
      }
      if (item.rate) {
        return toCurrency(item.rate, item.currency);
      }
      return toCurrency(parseFloat(item.wages) / parseFloat(item.hours), item.currency);
    },
  },
  {
    export: false,
    key: 'remove_shift',
    label: '',
    tdClass: 'text-right',
    thStyle: { width: '10%' },
  },
];

export const NET_PAY_SHIFT_FIELD_DEFINITIONS = [
  {
    key: 'employee_code',
    label: 'Employee Code',
  },
  {
    key: 'full_name',
    label: 'Employee',
    sortable: false,
  },
  {
    key: 'worked_on',
    label: 'Date',
    formatter: value => {
      if (value) {
        return toFormattedDate(value);
      }
    },
  },
  {
    key: 'wages',
    label: 'Earned Wages',
    formatter: (value, _key, item) => toCurrency(value, item.currency),
  },
  {
    key: 'net_total',
    label: 'Projected Net Pay',
    formatter: (value, _key, item) => toCurrency(value, item.currency),
  },
  {
    key: 'gross_total',
    label: 'Projected Gross Pay',
    formatter: (value, _key, item) => toCurrency(value, item.currency),
  },
  {
    key: 'shift_type',
    label: 'Type',
  },
];

export const SHIFT_FIELD_KEYS = {
  EMPLOYEE_CODE: 'employee_code',
  FULL_NAME: 'full_name',
  WORKED_ON: 'worked_on',
  HOURS: 'hours',
  WAGES: 'wages',
  TIPS: 'tips',
  WAGES_PER_HOUR: 'wages_per_hour',
  REMOVE_SHIFT: 'remove_shift',
};

export const NET_PAY_SHIFT_FIELD_KEYS = {
  EMPLOYEE_CODE: 'employee_code',
  FULL_NAME: 'full_name',
  WORKED_ON: 'worked_on',
  WAGES: 'wages',
  NET_TOTAL: 'net_total',
  GROSS_TOTAL: 'gross_total',
  SHIFT_TYPE: 'shift_type',
};

export const SHIFT_FILTER_KEYS = {
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  PAY_SCHEDULE: 'pay_schedule',
  PAY_SCHEDULE_DATE: 'pay_schedule_date',
  SALARY_OR_HOURLY: 'salary_or_hourly',
  SOURCE: 'source',
  DIVISIONS: 'divisions',
};

// wages is stored/filtered in integer minor units; scale the user-entered major-unit value before sending. hours is unscaled.
export const SHIFT_MONEY_FILTER_SCALE = { wages: 100 };

// v1 param names that shifts-v2 spells differently. It ignores unknown keys silently, so an unmapped
// name drops the filter (company-wide totals) instead of erroring — rename before sending.
const SHIFT_V2_FIELD_ALIASES = { [SHIFT_FILTER_KEYS.PAY_SCHEDULE]: 'pay_schedule_id' };

export const toShiftsV2Params = (params = {}) =>
  Object.entries(params).reduce((acc, [key, value]) => {
    acc[SHIFT_V2_FIELD_ALIASES[key] || key] = value;
    return acc;
  }, {});

// Spelled literally rather than imported: constants.js would otherwise pull in AppInput.vue transitively.
const WORKED_ON_FROM = 'worked_on[gte]';
const WORKED_ON_TO = 'worked_on[lte]';

// shifts-v2 applies worked_on inside its startDate/endDate scan window (default: last 60 days), so a
// date filter must widen the window too — asking for one real shift from 90 days ago returns nothing.
export const withShiftsV2DateWindow = (params = {}) => {
  const on = params.worked_on;
  const from = params[WORKED_ON_FROM] || on;
  const to = params[WORKED_ON_TO] || on;

  // Both bounds or neither: startDate alone lets the backend default the other end to today and blow
  // the 62-day cap, endDate alone silently keeps the default start. The picker must require both.
  if (!from || !to) {
    return params;
  }

  // Never override a parent-pinned window, matching the fixedFilters precedence in apiParams.
  return {
    ...params,
    ...(params.startDate ? null : { startDate: from }),
    ...(params.endDate ? null : { endDate: to }),
  };
};
